import { useMemo, useState } from 'react';

import type { GenerateWorkingNotesData } from './utils';
import type { AxiosResponse } from 'axios';

import { useLocalStorage } from 'usehooks-ts';

import { authAxios, IS_PROD } from '../../utils';

import {
    BUYING_BACKEND_ORIGIN,
    generateWorkingNotes,
    SERVICE_NOW_ORIGIN_PROD,
    SERVICE_NOW_ORIGIN_TEST,
} from './utils';

let serviceNowOrigin = SERVICE_NOW_ORIGIN_TEST;
if (IS_PROD) serviceNowOrigin = SERVICE_NOW_ORIGIN_PROD;

interface ServiceNowResponse {
    /** The number of the ticket that was created in ServiceNow */
    number: string;

    /** The system id of the ticket that was created in ServiceNow*/
    sysId: string;
}

interface UseServiceNowType {
    /** An array of ServiceNowResponses that have been created using this hook (stored in local storage) */
    ticketHistory: Array<ServiceNowResponse>;
    /** A boolean that is true when a ticket is being created */
    loading: boolean;
    /** A function that opens a ticket in ServiceNow in a new tab */
    openTicket: (sysId: string, target?: string) => void;
    /** A promise function that creates a ticket in ServiceNow */
    createTicket: (
        userEmail: string,
        title: string,
        description: string,
        data?: GenerateWorkingNotesData,
        overwriteWorkNotes?: string
    ) => Promise<ServiceNowResponse>;
}

/**
 * Possible values for assignment group
 * These id's are directly from service now
 */
enum AssignmentGroup {
    techBom = 'c270a0673b3b229099ed6a54c3e45a0c',
    buying = '994b7669db9a270032fe9ea9db961901',
}

interface UseServiceNowOptions {
    /** The idToken to use for authentication */
    idToken: string | undefined;
    /** An optional branch to use for creating tickets */
    supportBffBranch: string | null;
    /** The team to assign the ticket to @default 'buying' */
    assignmentGroup?: keyof typeof AssignmentGroup;
    /** Controls whether created tickets are automatically opened in a new tab @default true */
    autoOpenTickets?: boolean;
}

/**
 * A hook that can be used to create and open tickets in ServiceNow.
 * All tickets that are created are automatically saved in localStorage.
 * To gain access to service now test and dev,
 * request access through the self service platform:
 * https://bestseller.service-now.com/bestsellersp?id=sc_cat_item&sys_id=440e418d97d2b9907f19f441f053afd0&spa=1
 */
const useServiceNow = ({
    idToken,
    supportBffBranch,
    assignmentGroup = 'buying',
    autoOpenTickets: autoOpenTicketsProp,
}: UseServiceNowOptions): UseServiceNowType => {
    const [ticketHistory, setTicketHistory] = useLocalStorage<
        Array<ServiceNowResponse>
    >('sn-ticket-history', []);
    const [loading, setLoading] = useState<boolean>(false);

    const autoOpenTickets = autoOpenTicketsProp ?? true;

    const createTicketUrl = useMemo(() => {
        const branchPrefix = supportBffBranch ? `/${supportBffBranch}` : '';
        return `${BUYING_BACKEND_ORIGIN}${branchPrefix}/support/create-incident`;
    }, [supportBffBranch]);

    const openTicket = (sysId: string, target = '_blank'): void => {
        window.open(
            `${serviceNowOrigin}/bestsellersp?id=ticketsp&table=incident&sys_id=${sysId}`,
            target
        );
    };

    const createTicket = async (
        userEmail: string,
        title: string,
        description: string,
        data?: GenerateWorkingNotesData,
        overwriteWorkNotes?: string
    ): Promise<ServiceNowResponse> => {
        if (!idToken) {
            throw new Error(
                'Cannot create a ServiceNow ticket without idToken'
            );
        }

        setLoading(true);

        /**
         * Working notes can only be supplied as a string, but luckily it supports newlines.
         * The default value always includes basic user info and a link to the datadog session replay.
         */
        const workingNotes =
            overwriteWorkNotes ??
            generateWorkingNotes({
                datadogSessionId: data?.datadogSessionId,
                datadogApplicationId: data?.datadogApplicationId,
                msalProfile: data?.msalProfile,
                buyingProfile: data?.buyingProfile,
                featureFlags: data?.featureFlags,
            });

        const body = {
            ['u_ref_']: userEmail,
            /** Prefills the category dropdown in ServiceNow */
            ['u_category']: 'BestOne',
            /** Prefills the subcategory dropdown in ServiceNow */
            ['u_subcategory']: 'Wholesale',
            ['caller_id']: userEmail,
            /** Although called description, this sets the title for the SNOW case */
            ['short_description']: title,
            description,
            /** Prefills which team is assigned to the SNOW case */
            ['assignment_group']: AssignmentGroup[assignmentGroup],
            ['work_notes']: workingNotes,
        };

        return authAxios
            .post<ServiceNowResponse>(createTicketUrl, body, {
                headers: {
                    ['SERVICENOW-API-VERSION']: '2.0',
                    provider: 'AD',
                },
            })
            .then(
                (
                    response: AxiosResponse<ServiceNowResponse>
                ): ServiceNowResponse => {
                    setTicketHistory((prev) => [...prev, response.data]);
                    if (!response.data.sysId) {
                        throw new Error('No sysId returned from ServiceNow');
                    }
                    if (autoOpenTickets) openTicket(response.data.sysId);
                    return response.data;
                }
            )
            .catch((error: unknown) => {
                throw error;
            })
            .finally((): void => {
                setLoading(false);
            });
    };

    return {
        ticketHistory,
        loading,
        createTicket,
        openTicket,
    };
};

export { useServiceNow };
export type { ServiceNowResponse };
